import { clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs))
}

// Local-timezone-safe date helpers. `new Date().toISOString().slice(0,10)`
// converts to UTC which yields the previous day in IST after midnight UTC —
// e.g. May 1st 00:00 IST becomes Apr 30 18:30 UTC. Use these instead.
export function todayISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

export function firstOfThisMonthISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}-01`;
}

// Format number as Indian Rupees
export function formatINR(amount) {
  if (amount === null || amount === undefined) return '₹0.00';
  
  const num = parseFloat(amount);
  if (isNaN(num)) return '₹0.00';
  
  const isNegative = num < 0;
  const absNum = Math.abs(num);
  
  // Format with Indian numbering system
  const formatted = absNum.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  
  return isNegative ? `-₹${formatted}` : `₹${formatted}`;
}

// Compact INR formatter for chart axes — ₹0, ₹85K, ₹3L, ₹2.5Cr.
// Indian numbering: 1 Lakh = 100,000; 1 Crore = 10,000,000.
export function formatINRShort(amount) {
  const n = parseFloat(amount);
  if (!isFinite(n)) return '₹0';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1e7) return `${sign}₹${(abs / 1e7).toFixed(abs >= 1e8 ? 0 : 1).replace(/\.0$/, '')}Cr`;
  if (abs >= 1e5) return `${sign}₹${(abs / 1e5).toFixed(abs >= 1e6 ? 0 : 1).replace(/\.0$/, '')}L`;
  if (abs >= 1e3) return `${sign}₹${(abs / 1e3).toFixed(0)}K`;
  return `${sign}₹${abs.toFixed(0)}`;
}

// Format percentage
export function formatPercent(value) {
  if (value === null || value === undefined) return '0%';
  return `${parseFloat(value).toFixed(1)}%`;
}

// Get margin status color
export function getMarginStatus(margin) {
  if (margin >= 30) return 'success';
  if (margin >= 20) return 'warning';
  return 'error';
}

// Get margin badge class
export function getMarginBadgeClass(margin) {
  if (margin >= 30) return 'badge-success';
  if (margin >= 20) return 'badge-warning';
  return 'badge-error';
}

// Format date
export function formatDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
}

// Get status badge class
export function getStatusBadgeClass(status) {
  const statusClasses = {
    draft: 'status-draft',
    sent: 'status-sent',
    accepted: 'status-accepted',
    rejected: 'status-rejected',
    completed: 'status-accepted',
    in_progress: 'status-sent',
    active: 'status-active',
    inactive: 'status-inactive'
  };
  return statusClasses[status] || 'badge-gray';
}

// Truncate text
export function truncate(str, length = 30) {
  if (!str) return '';
  return str.length > length ? str.substring(0, length) + '...' : str;
}

// Generate short ID
export function shortId(id) {
  if (!id) return '';
  return id.substring(0, 8).toUpperCase();
}

// Debounce function
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Department colors for charts (grayscale)
export const departmentColors = {
  'Branding': '#000000',
  'Marketing': '#333333',
  'Operations': '#555555',
  'Sales': '#777777',
  'Video Editing': '#999999',
  'Designing': '#444444',
  'Video Production': '#666666',
  'Advertising': '#888888'
};

// Chart colors (grayscale palette)
export const chartColors = [
  '#000000',
  '#333333',
  '#555555',
  '#777777',
  '#999999',
  '#bbbbbb'
];

// Human-readable message from an axios error. FastAPI 422s return `detail` as an
// array of {msg}; passing that straight to toast.error crashed the app.
export function apiError(err, fallback = 'Something went wrong') {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || String(x)).join('; ');
  if (err?.response?.status === 403) return 'You do not have permission to do that.';
  return fallback;
}
